import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Wand2, QrCode, Loader2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { GenerationStatus } from '../types';
import { Property } from '../types';
import { supabase } from '../lib/supabase';

interface Props {
  images: { url: string; label: string }[];
  currentIndex: number;
  setError: (error: string | null) => void;
  setLightbox: (lightbox: { images: { url: string; label: string }[]; currentIndex: number; property?: Property } | null) => void;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onGenerateImage?: () => void;
  error?: string | null;
  property?: Property;
}

const REFRESH_INTERVAL = 5000; // 5 seconds

const ImageLightbox: React.FC<Props> = ({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrevious,
  setError,
  setLightbox,
  onGenerateImage,
  error,
  property
}) => {
  const [refreshedImages, setRefreshedImages] = React.useState(images);
  const [refreshedProperty, setRefreshedProperty] = React.useState(property);
  const [generatingAll, setGeneratingAll] = useState(false);

  const [generatingStyles, setGeneratingStyles] = useState<Record<string, boolean>>({
    pencil: false,
    oil: false,
    charcoal: false,
    chalk: false
  });

  // Update refreshed images when property changes
  React.useEffect(() => {
    if (property) {
      const newImages = property.original_image ? [{ url: property.original_image, label: 'Original' }] : [];
      
      if (property.pencil_image_url?.trim()) {
        newImages.push({ url: property.pencil_image_url, label: 'Pencil Style' });
      }
      if (property.oil_image_url?.trim()) {
        newImages.push({ url: property.oil_image_url, label: 'Oil Style' });
      }
      if (property.charcoal_image_url?.trim()) {
        newImages.push({ url: property.charcoal_image_url, label: 'Charcoal Style' });
      }
      if (property.chalk_image_url?.trim()) {
        newImages.push({ url: property.chalk_image_url, label: 'Chalk Style' });
      }
      setRefreshedImages(newImages);
    }
  }, [property]);

  // Function to refresh property data
  const refreshPropertyData = React.useCallback(async () => {
    if (!property) return;

    try {
      const { data: updatedProperty } = await supabase
        .from('properties')
        .select('*')
        .eq('id', property.id)
        .single();

      if (updatedProperty) {
        setRefreshedProperty(updatedProperty);
        
        // Update images array with new URLs
        const newImages = [];
        if (updatedProperty.original_image) {
          newImages.push({ url: updatedProperty.original_image, label: 'Original' });
        }
        if (updatedProperty.pencil_image_url?.trim()) {
          newImages.push({ url: updatedProperty.pencil_image_url, label: 'Pencil Style' });
        }
        if (updatedProperty.oil_image_url?.trim()) {
          newImages.push({ url: updatedProperty.oil_image_url, label: 'Oil Style' });
        }
        if (updatedProperty.charcoal_image_url?.trim()) {
          newImages.push({ url: updatedProperty.charcoal_image_url, label: 'Charcoal Style' });
        }
        if (updatedProperty.chalk_image_url?.trim()) {
          newImages.push({ url: updatedProperty.chalk_image_url, label: 'Chalk Style' });
        }
        
        setRefreshedImages(newImages);
      }
    } catch (error) {
      console.error('Error refreshing property data:', error);
    }
  }, [property]);

  // Set up refresh interval
  React.useEffect(() => {
    const interval = setInterval(refreshPropertyData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshPropertyData]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') onNext();
    if (e.key === 'ArrowLeft') onPrevious();
    
    // Prevent save shortcuts (Ctrl+S, Cmd+S)
    if ((e.ctrlKey || e.metaKey) && e.key === 's') {
      e.preventDefault();
      console.log('Save disabled for image protection');
    }
  }, [onClose, onNext, onPrevious]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!images.length) return null;

  // Ensure we have a valid current image with default values
  const currentImage = refreshedImages[currentIndex] || images[currentIndex] || { url: '', label: 'Loading...' };
  const qrCodeUrl = property ? 
    `https://courageous-gingersnap-4f44cf.netlify.app/?code=${property.id}` : '';
  
  // Find the current variant type with null checks
  const currentVariant = refreshedProperty ? [
    { url: refreshedProperty.pencil_image_url || '', label: 'Pencil' },
    { url: refreshedProperty.oil_image_url || '', label: 'Oil' },
    { url: refreshedProperty.charcoal_image_url || '', label: 'Charcoal' },
    { url: refreshedProperty.chalk_image_url || '', label: 'Chalk' }
  ].find(variant => variant.url && currentImage?.url && variant.url === currentImage.url) : null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
      >
        <X className="w-8 h-8" />
      </button>
      
      {images.length > 1 && (
        <>
          <button
            onClick={onPrevious}
            className={`absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-75 transition-all ${
              currentIndex === 0 || refreshedImages.length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
            }`}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          
          <button
            onClick={onNext}
            className={`absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 p-2 rounded-full text-white hover:bg-opacity-75 transition-all ${
              currentIndex === refreshedImages.length - 1 ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110'
            }`}
            disabled={currentIndex === refreshedImages.length - 1}
          >
            <ChevronRight className="w-8 h-8" />
          </button>
        </>
      )}
      <div className="max-w-[90vw] max-h-[90vh] px-16 flex flex-col">
        <div className="relative">
          {currentImage.label === 'Original' && refreshedProperty && (
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button
                onClick={async () => {
                  if (generatingAll) return;
                  setError(null);
                  setGeneratingAll(true);
                  
                  try {
                    // Update status to pending
                    const { error: statusError } = await supabase
                      .from('properties')
                      .update({ status: 'pending' })
                      .eq('id', refreshedProperty.id);

                    if (statusError) throw statusError;

                    // Trigger all webhooks in parallel
                    const webhooks = [
                      { url: 'https://hook.us2.make.com/w9x96v7yf5hnosl8rrpaf33f7imqa28h', style: 'pencil' },
                      { url: 'https://hook.us2.make.com/hh1dr1yn7pdbwba79836l8sm2z33y6jh', style: 'oil' },
                      { url: 'https://hook.us2.make.com/7rq24opwxtt9nifhr1fm8f9z7tyuug7x', style: 'charcoal' },
                      { url: 'https://hook.us2.make.com/canfn06ate87ruc9c3qycyve1bqjnsy0', style: 'chalk' }
                    ];

                    await Promise.all(webhooks.map(async ({ url, style }) => {
                      const response = await fetch(url, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                          id: refreshedProperty.id,
                          image_url: refreshedProperty.original_image || null,
                          color: refreshedProperty.color || null,
                          prompt: refreshedProperty.prompt || null
                        })
                      });

                      // Parse response for more detailed error info
                      let errorDetails;
                      try {
                        errorDetails = await response.text();
                      } catch (e) {
                        errorDetails = 'No additional error details available';
                      }

                      if (!response.ok) {
                        throw new Error(
                          `Failed to trigger ${style} generation: ${response.status} ${response.statusText}. ${errorDetails}`
                        );
                      }

                      console.log(`Successfully triggered ${style} generation`);
                    }));

                  } catch (error) {
                    const errorMessage = error instanceof Error 
                      ? error.message 
                      : 'Failed to trigger image generation';
                    console.error('Generation error:', errorMessage);
                    setError(errorMessage);
                    
                    // Update property status to failed
                    await supabase
                      .from('properties')
                      .update({ status: 'failed' })
                      .eq('id', refreshedProperty.id);
                  } finally {
                    setGeneratingAll(false);
                  }
                }}
                className={`inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg transition-colors ${
                  generatingAll ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-700'
                }`}
                disabled={generatingAll}
              >
                {generatingAll ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Generating All...</span>
                  </>
                ) : (
                  <>
                    <Wand2 className="w-4 h-4" />
                    <span>Generate All Images</span>
                  </>
                )}
              </button>
            </div>
          )}
          {error && (
            <div className="absolute top-4 left-4 z-10 bg-red-50 text-red-600 px-4 py-2 rounded-lg">
              {error}
            </div>
          )}
          <div 
            className="relative mx-auto"
            style={{ 
              display: 'inline-block',
              maxWidth: '37%',
              maxHeight: '37.5vh'
            }}
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
          >
            <img
              src={currentImage.url}
              alt={currentImage.label}
              className="w-full h-full object-contain select-none pointer-events-none block"
              draggable="false"
              style={{ position: 'relative', zIndex: 1 }}
            />
            {/* 50% Transparent overlay (50% opacity for testing) - physically blocks all image interactions */}
            <div 
              className="absolute cursor-default"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{ 
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)', // 50% black tint (50% transparent)
                userSelect: 'none', 
                WebkitUserSelect: 'none', 
                msUserSelect: 'none',
                pointerEvents: 'auto', // Allow the overlay to capture all pointer events
                zIndex: 10 // Ensure overlay is on top
              }}
            />
          </div>
        </div>
        
        {/* Thumbnails */}
        {refreshedProperty && (
          <div className="flex justify-center gap-4 mt-4">
            {[
              { url: refreshedProperty.original_image, label: 'Original' },
              { url: refreshedProperty.pencil_image_url || '', label: 'Pencil' },
              { url: refreshedProperty.oil_image_url || '', label: 'Oil' },
              { url: refreshedProperty.charcoal_image_url || '', label: 'Charcoal' },
              { url: refreshedProperty.chalk_image_url || '', label: 'Chalk' }
            ].map((variant, index) => (
              <button
                key={variant.label}
                onClick={() => {
                  if (variant.url) {
                    // Find the index of this variant in the images array
                    const variantIndex = refreshedImages.findIndex(img => 
                      img.url.trim() === variant.url?.trim()
                    );
                    
                    if (variantIndex !== -1) {
                      setLightbox(prev => prev ? {
                        ...prev,
                        currentIndex: variantIndex
                      } : null);
                    }
                  }
                }}
                className={`relative w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                  variant.url ? 'hover:opacity-80' : 'bg-gray-100'
                } ${
                  (variant.url && currentImage?.url?.trim().replace(/\/$/, '') === variant.url?.trim().replace(/\/$/, ''))
                    ? 'border-blue-500 ring-2 ring-blue-200'
                    : 'border-gray-200'
                }`}
              >
                {variant.url ? (
                  <img 
                    src={variant.url} 
                    alt={variant.label} 
                    className="w-full h-full object-cover select-none pointer-events-none" 
                    draggable="false"
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                    {(variant.label === 'Pencil' || variant.label === 'Oil' || variant.label === 'Charcoal' || variant.label === 'Chalk') && refreshedProperty && (
                      <div
                        className="relative"
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          className={`px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors ${
                            generatingStyles[variant.label.toLowerCase()] ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          aria-disabled={generatingStyles[variant.label.toLowerCase()]}
                          onClick={async () => {
                            if (generatingStyles[variant.label.toLowerCase()]) return;
                            setError(null);
                            const style = variant.label.toLowerCase();
                            setGeneratingStyles(prev => ({ ...prev, [style]: true }));
                          
                            try {
                              // First update the status to pending
                              const { error: statusError } = await supabase
                                .from('properties')
                                .update({ status: 'pending' })
                                .eq('id', refreshedProperty.id);

                              if (statusError) throw statusError;

                              // Then trigger the webhook
                              const response = await fetch(variant.label === 'Oil' 
                                ? 'https://hook.us2.make.com/hh1dr1yn7pdbwba79836l8sm2z33y6jh'
                                : variant.label === 'Charcoal'
                                ? 'https://hook.us2.make.com/7rq24opwxtt9nifhr1fm8f9z7tyuug7x'
                                : variant.label === 'Chalk'
                                ? 'https://hook.us2.make.com/canfn06ate87ruc9c3qycyve1bqjnsy0'
                                : 'https://hook.us2.make.com/w9x96v7yf5hnosl8rrpaf33f7imqa28h', {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  id: refreshedProperty.id,
                                  image_url: refreshedProperty.original_image || null,
                                  color: refreshedProperty.color || null,
                                  prompt: refreshedProperty.prompt || null
                                })
                              });

                              // Parse response for more detailed error info
                              let errorDetails;
                              try {
                                errorDetails = await response.text();
                              } catch (e) {
                                errorDetails = 'No additional error details available';
                              }

                              if (!response.ok) {
                                throw new Error(
                                  `Failed to trigger ${variant.label} generation: ${response.status} ${response.statusText}. ${errorDetails}`
                                );
                              }

                              // Successful response
                              console.log(`Successfully triggered ${variant.label} generation`);
                            } catch (error) {
                              const errorMessage = error instanceof Error 
                                ? error.message 
                                : `Failed to generate ${variant.label.toLowerCase()} style`;
                              console.error('Generation error:', errorMessage);
                              setError(errorMessage);
                              
                              // Update property status to failed
                              await supabase
                                .from('properties')
                                .update({ status: 'failed' })
                                .eq('id', refreshedProperty.id);
                            } finally {
                              // Always reset the generating state
                              setGeneratingStyles(prev => ({ ...prev, [style]: false }));
                            }
                          }}
                      >
                        {generatingStyles[variant.label.toLowerCase()] ? (
                          <div className="flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span>Generating...</span>
                          </div>
                        ) : (
                          <span>Generate</span>
                        )}
                      </div>
                      </div>
                    )}
                    <span className="text-gray-400 text-sm">{variant.label}</span>
                  </div>
                )}
              </button>
            ))}
            {/* QR Code Button and Preview */}
            <button
              onClick={() => {
                if (qrCodeUrl) {
                  setLightbox(prev => prev ? {
                    ...prev,
                    currentIndex: refreshedImages.length
                  } : null);
                }
              }}
              className={`relative w-24 h-24 rounded-lg overflow-hidden border-2 transition-all bg-white ${
                currentIndex === refreshedImages.length
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : 'border-gray-200'
              }`}
            >
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <QrCode className="w-8 h-8 text-gray-400 mb-1" />
                <span className="text-xs text-gray-500">QR Code</span>
              </div>
            </button>
          </div>
        )}
        <div className="text-white text-center mt-4">
          <p className="text-lg">{currentImage?.label || 'Loading...'}</p>
          <p className="text-sm text-gray-400 mt-1">
            {currentIndex + 1} of {(refreshedImages.length || images.length) + 1}
          </p>
        </div>
      </div>
      {/* QR Code View */}
      {currentIndex === refreshedImages.length && qrCodeUrl && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Property QR Code</h3>
              <button
                onClick={() => setLightbox(prev => prev ? { ...prev, currentIndex: 0 } : null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex justify-center mb-6">
              <QRCodeSVG
                value={qrCodeUrl}
                size={256}
                level="H"
                includeMargin
                className="w-full max-w-[256px]"
              />
            </div>
            <p className="text-sm text-gray-600 text-center break-all">
              {qrCodeUrl}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageLightbox;